import { useState, useEffect } from 'react';
import Alert from '../common/Alert';
import SelectField from '../common/SelectField.js';
import vehicleService from '../../services/vehicleService.js';
import vehicleOwnerService from '../../services/VehicleOwnerService.js';
import { inputFilters } from '../common/Validation.js';
import { VEHICLE_TYPE_OPTIONS } from '../../utils/vehicleTypes.js';


const EMPTY_VEHICLE = {
  vehicle_number: '',
  vehicle_name: '',
  vehicle_type: 'cargo_goods',
  vehicle_size: '',
  capacity: '',
  vehicle_contract_amount: '',
};

const EMPTY_OWNER = {
  owner_name: '',
  contact: '',
  email: '',
  owner_percentage: '',
};

export default function CreateVehicleWithOwnerModal({
  open,
  onClose,
  onSuccess,
  initialVehicleNumber = '',
  companyAccess = {},
}) {
  const hasOnContract = Boolean(companyAccess.on_contract);
  const hasCommission = Boolean(companyAccess.commission);

  const [submitting, setSubmitting] = useState(false);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [vehicleOwners, setVehicleOwners] = useState([]);
  const [ownerMode, setOwnerMode] = useState('existing');
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [ownerPercentageOverride, setOwnerPercentageOverride] = useState('');
  const [vehicleData, setVehicleData] = useState({ ...EMPTY_VEHICLE });
  const [newOwnerData, setNewOwnerData] = useState({ ...EMPTY_OWNER });

  useEffect(() => {
    if (!open) return;
    setVehicleData({ ...EMPTY_VEHICLE, vehicle_number: initialVehicleNumber || '' });
    setNewOwnerData({ ...EMPTY_OWNER });
    setOwnerMode('existing');
    setSelectedOwnerId('');
    setOwnerPercentageOverride('');
    loadOwners();
  }, [open, initialVehicleNumber]);

  const loadOwners = async () => {
    try {
      setOwnersLoading(true);
      const res = await vehicleOwnerService.getAll(1, 500);
      const list = res?.data || res || [];
      setVehicleOwners(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('Failed to load vehicle owners:', err);
      Alert.error('Failed to load vehicle owners');
    } finally {
      setOwnersLoading(false);
    }
  };

  const handleVehicleChange = (e) => {
    const { name, value } = e.target;
    let filtered = value;
    if (name === 'vehicle_number') filtered = value.toUpperCase();
    else if (['vehicle_contract_amount', 'vehicle_size', 'capacity'].includes(name)) filtered = inputFilters.amount(value);
    setVehicleData((prev) => ({ ...prev, [name]: filtered }));
  };

  const handleOwnerChange = (e) => {
    const { name, value } = e.target;
    let filtered = value;
    if (name === 'contact') filtered = inputFilters.digits10(value);
    else if (name === 'owner_percentage') filtered = inputFilters.amount(value);
    setNewOwnerData((prev) => ({ ...prev, [name]: filtered }));
  };

  const selectedOwner = vehicleOwners.find(
    (o) => o.vehicle_owner_id === selectedOwnerId
  );

  const needsOwnerPercentage =
    hasCommission &&
    ownerMode === 'existing' &&
    selectedOwner &&
    (selectedOwner.owner_percentage === null || selectedOwner.owner_percentage === undefined);

  const handleSubmit = async () => {
    if (!vehicleData.vehicle_number.trim()) {
      Alert.error('Vehicle number is required');
      return;
    }
    if (!vehicleData.vehicle_name.trim()) {
      Alert.error('Vehicle name is required');
      return;
    }
    if (hasOnContract && !vehicleData.vehicle_contract_amount) {
      Alert.error('Contract amount is required for on-contract vehicles');
      return;
    }

    if (ownerMode === 'existing') {
      if (!selectedOwnerId) {
        Alert.error('Please select a vehicle owner');
        return;
      }
      if (needsOwnerPercentage && !ownerPercentageOverride) {
        Alert.error('Owner percentage is required for shipment commission');
        return;
      }
    } else {
      if (!newOwnerData.owner_name.trim()) {
        Alert.error('Owner name is required');
        return;
      }
      if (!newOwnerData.contact.trim()) {
        Alert.error('Owner contact is required');
        return;
      }
      if (hasCommission && !newOwnerData.owner_percentage) {
        Alert.error('Owner percentage is required for shipment commission');
        return;
      }
    }

    try {
      setSubmitting(true);

      const vehicleFormData = new FormData();
      vehicleFormData.append('vehicle_name', vehicleData.vehicle_name.trim());
      vehicleFormData.append('vehicle_number', vehicleData.vehicle_number.trim().toUpperCase());
      vehicleFormData.append('vehicle_type', vehicleData.vehicle_type);
      if (vehicleData.vehicle_size) {
        vehicleFormData.append('vehicle_size', vehicleData.vehicle_size);
      }
      if (vehicleData.capacity) {
        vehicleFormData.append('capacity', vehicleData.capacity);
      }
      if (hasOnContract && vehicleData.vehicle_contract_amount) {
        vehicleFormData.append('vehicle_contract_amount', vehicleData.vehicle_contract_amount);
      }
      vehicleFormData.append('status', 'active');

      const vehicleResponse = await vehicleService.createVehicle(vehicleFormData);
      const createdVehicle = vehicleResponse.data?.data || vehicleResponse.data;
      const newVehicleId = createdVehicle.id || createdVehicle.vehicle_id;

      if (ownerMode === 'existing') {
        const owner = selectedOwner || vehicleOwners.find((o) => o.vehicle_owner_id === selectedOwnerId);
        const existingVehicles = (owner?.vehicles || []).map((v) => Number(v));
        const updatedVehicles = existingVehicles.includes(Number(newVehicleId))
          ? existingVehicles
          : [...existingVehicles, Number(newVehicleId)];

        const updatePayload = { vehicles: updatedVehicles };
        if (needsOwnerPercentage && ownerPercentageOverride) {
          updatePayload.owner_percentage = Number(ownerPercentageOverride);
        }

        await vehicleOwnerService.update(selectedOwnerId, updatePayload);
      } else {
        await vehicleOwnerService.create({
          owner_name: newOwnerData.owner_name.trim(),
          contact: newOwnerData.contact.trim(),
          email: newOwnerData.email.trim() || undefined,
          vehicles: [Number(newVehicleId)],
          owner_percentage:
            newOwnerData.owner_percentage !== ''
              ? Number(newOwnerData.owner_percentage)
              : null,
        });
      }

      const ownerName =
        ownerMode === 'existing'
          ? (selectedOwner || vehicleOwners.find((o) => o.vehicle_owner_id === selectedOwnerId))
              ?.owner_name
          : newOwnerData.owner_name.trim();

      Alert.success('Vehicle created and assigned to owner');
      onSuccess({
        ...createdVehicle,
        owner_name: ownerName || undefined,
      });
      onClose();
    } catch (error) {
      console.error('Error creating vehicle with owner:', error);
      Alert.error(error.response?.data?.error || 'Failed to create vehicle');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 space-y-5">
          <h3 className="text-lg font-semibold text-secondary">Create Vehicle & Assign Owner</h3>

          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Vehicle details</p>
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">
                Vehicle Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="vehicle_number"
                value={vehicleData.vehicle_number}
                onChange={handleVehicleChange}
                placeholder="e.g., GJ01AB1234"
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm uppercase"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">
                Vehicle Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="vehicle_name"
                value={vehicleData.vehicle_name}
                onChange={handleVehicleChange}
                placeholder="e.g., Tata 407"
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-secondary mb-1">Vehicle Type</label>
              <SelectField variant="form"
                name="vehicle_type"
                value={vehicleData.vehicle_type}
                onChange={handleVehicleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
              >
                {VEHICLE_TYPE_OPTIONS.map((type) => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </SelectField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Vehicle Size (Ft)</label>
                <input
                  type="text"
                  name="vehicle_size"
                  value={vehicleData.vehicle_size}
                  onChange={handleVehicleChange}
                  placeholder="e.g., 20"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Capacity (MT)</label>
                <input
                  type="text"
                  name="capacity"
                  value={vehicleData.capacity}
                  onChange={handleVehicleChange}
                  placeholder="e.g., 9"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                />
              </div>
            </div>
            {hasOnContract && (
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">
                  Contract Amount (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="vehicle_contract_amount"
                  value={vehicleData.vehicle_contract_amount}
                  onChange={handleVehicleChange}
                  placeholder="Enter contract amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                />
              </div>
            )}
          </div>

          <div className="space-y-3 border-t pt-4">
            <p className="text-sm font-medium text-gray-700">Vehicle owner</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="ownerMode"
                  checked={ownerMode === 'existing'}
                  onChange={() => setOwnerMode('existing')}
                />
                Existing owner
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="ownerMode"
                  checked={ownerMode === 'new'}
                  onChange={() => setOwnerMode('new')}
                />
                New owner
              </label>
            </div>

            {ownerMode === 'existing' ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Select Owner <span className="text-red-500">*</span>
                  </label>
                  <SelectField variant="form"
                    value={selectedOwnerId}
                    onChange={(e) => {
                      setSelectedOwnerId(e.target.value);
                      setOwnerPercentageOverride('');
                    }}
                    disabled={ownersLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                  >
                    <option value="">Select vehicle owner</option>
                    {vehicleOwners.map((owner) => (
                      <option key={owner.vehicle_owner_id} value={owner.vehicle_owner_id}>
                        {owner.owner_name} ({owner.contact})
                      </option>
                    ))}
                  </SelectField>
                </div>
                {needsOwnerPercentage && (
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-1">
                      Owner Percentage (%) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={ownerPercentageOverride}
                      onChange={(e) => setOwnerPercentageOverride(inputFilters.amount(e.target.value))}
                      placeholder="0–100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Required for shipment commission — this owner has no percentage set.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Owner Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="owner_name"
                    value={newOwnerData.owner_name}
                    onChange={handleOwnerChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Contact <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="contact"
                    value={newOwnerData.contact}
                    onChange={handleOwnerChange}
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Email (Optional)</label>
                  <input
                    type="email"
                    name="email"
                    value={newOwnerData.email}
                    onChange={handleOwnerChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                  />
                </div>
                {hasCommission && (
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-1">
                      Owner Percentage (%) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="owner_percentage"
                      value={newOwnerData.owner_percentage}
                      onChange={handleOwnerChange}
                      placeholder="0–100"
                      className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400 text-sm"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 font-medium text-sm disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create & Assign'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 font-medium text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
